"use client";

import {useUiControlLabels} from "@/components/i18n/ui-control-labels-context"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  Bot,
  Tag,
  Clock,
  Mail,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  User,
  FileText,
  ExternalLink,
  FileEdit,
  MessageSquare,
  Send,
  PlayCircle,
  CheckCheck,
  Pencil,
  X,
  Forward,
  SkipForward,
  UserCheck,
  UserX,
} from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/hooks/auth/use-auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import type { AutoReplyDetails } from "@/lib/agents/email/auto-reply-details";
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




type AIFeedback = {
  id: string;
  feedback_text: string;
  status: "pending" | "in_progress" | "resolved";
  created_at: string;
  resolved_at: string | null;
};

interface AutoReplyViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  autoReply: AutoReplyDetails | null;
  /** Stamped on new feedback; without it the QA page's agent filter never finds the row. */
  agentId?: string;
  onFeedbackUpdate?: () => void;
  userTimezone?: string;
  showCrmLink?: boolean;
}

function getQualificationColor(label: string) {
  const colors: Record<string, string> = {
    "Qualified Lead": "bg-green-100 text-green-800 border-green-200",
    "Support Request": "bg-blue-100 text-blue-800 border-blue-200",
    Spam: "bg-red-100 text-red-800 border-red-200",
    Other: "bg-gray-100 text-gray-800 border-gray-200",
  };
  return colors[label] || "bg-purple-100 text-purple-800 border-purple-200";
}

function formatDateTz(dateString: string | null, timezone: string | undefined, tUi: ReturnType<typeof useTranslations<"Email.AgentsEmailDialogsAutoReplyViewerDialog">>, dateLocale: string = "en-US") {
  if (!dateString) return tUi("unknownDate");
  try {
    const opts: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      ...(timezone ? { timeZone: timezone } : {}),
    };
    return new Date(dateString).toLocaleDateString(dateLocale, opts);
  } catch {
    return dateString;
  }
}

export function AutoReplyViewerDialog({
  open,
  onOpenChange,
  autoReply,
  agentId,
  onFeedbackUpdate,
  userTimezone,
  showCrmLink,
}: AutoReplyViewerDialogProps) {
  const {locale: uiDateLocale} = useUiControlLabels()


  const tUi = useTranslations("Email.AgentsEmailDialogsAutoReplyViewerDialog")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth();
  const [threadHistoryExpanded, setThreadHistoryExpanded] = useState(false);
  const [originalEmailExpanded, setOriginalEmailExpanded] = useState(false);
  const [feedbackExpanded, setFeedbackExpanded] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedback, setFeedback] = useState<AIFeedback | null>(null);
  const [isEditingFeedback, setIsEditingFeedback] = useState(false);
  const [editFeedbackText, setEditFeedbackText] = useState("");

  const loadFeedback = useCallback(async () => {
    if (!autoReply?.id) return;

    try {
      setLoadingFeedback(true);
      const res = await fetchWithAuth(
        `/api/ai/feedback?reference_type=auto_reply&reference_id=${autoReply.id}&limit=1`
      );

      if (!res.ok) throw new Error("Failed to load feedback");

      const data = await res.json();
      setFeedback(data.items?.[0] || null);
    } catch (error) {
      console.error("Failed to load feedback:", error);
    } finally {
      setLoadingFeedback(false);
    }
  }, [autoReply?.id, fetchWithAuth]);

  // Load feedback for this auto-reply when dialog opens
  useEffect(() => {
    if (open && autoReply?.id) {
      loadFeedback();
    } else {
      setFeedback(null);
      setIsEditingFeedback(false);
      setEditFeedbackText("");
    }
  }, [open, autoReply?.id, loadFeedback]);

  const handleSaveFeedback = async () => {
    if (!autoReply) return;

    try {
      setSavingFeedback(true);
      const res = await fetchWithAuth("/api/ai/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          reference_type: "auto_reply",
          reference_id: autoReply.id,
          feedback_text: feedbackText,
          agent_type: "email_agent",
          ...(agentId ? { agent_id: agentId } : {}),
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to save feedback");
      }

      const data = await res.json();
      toast.success(tUi("feedbackSaved"));
      setFeedback(data.feedback);
      setFeedbackText("");

      if (onFeedbackUpdate) {
        onFeedbackUpdate();
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save feedback"
      );
    } finally {
      setSavingFeedback(false);
    }
  };

  const handleUpdateStatus = async (newStatus: "in_progress" | "resolved") => {
    if (!feedback) return;

    try {
      setUpdatingStatus(true);
      const res = await fetchWithAuth(`/api/ai/feedback/${feedback.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to update status");
      }

      const data = await res.json();
      toast.success(
        newStatus === "resolved"
          ? "Marked as resolved"
          : "Marked as in progress"
      );
      setFeedback(data.feedback);

      if (onFeedbackUpdate) {
        onFeedbackUpdate();
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update status"
      );
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleEditFeedback = () => {
    if (feedback) {
      setEditFeedbackText(feedback.feedback_text);
      setIsEditingFeedback(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingFeedback(false);
    setEditFeedbackText("");
  };

  const handleSaveEditedFeedback = async () => {
    if (!feedback || !editFeedbackText.trim()) return;

    try {
      setSavingFeedback(true);
      const res = await fetchWithAuth(`/api/ai/feedback/${feedback.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ feedback_text: editFeedbackText }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to update feedback");
      }

      const data = await res.json();
      toast.success(tUi("feedbackUpdated"));
      setFeedback(data.feedback);
      setIsEditingFeedback(false);
      setEditFeedbackText("");

      if (onFeedbackUpdate) {
        onFeedbackUpdate();
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update feedback"
      );
    } finally {
      setSavingFeedback(false);
    }
  };

  const getFeedbackStatusBadge = (status: string | null | undefined) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
            <span key={tUi("pending")}>{tUi("pending")}</span></Badge>
        );
      case "in_progress":
        return (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            <span key={tUi("inProgress")}>{tUi("inProgress")}</span></Badge>
        );
      case "resolved":
        return (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            <span key={tUi("resolved")}>{tUi("resolved")}</span></Badge>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-lg font-semibold pr-8">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-purple-600" />
              <span><span key={tUi("autoReplyDetails")}>{tUi("autoReplyDetails")}</span></span>
              {autoReply?.id && (
                <span className="text-xs font-normal text-muted-foreground">
                  #{autoReply.id}
                </span>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        {!autoReply ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-600"><span key={tUi("loadingAutoReply")}>{tUi("loadingAutoReply")}</span></span>
          </div>
        ) : (
          <div className="overflow-y-auto max-h-[calc(90vh-120px)] pr-2">
            <div className="space-y-6">
              {/* Status Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">
                      <span key={tUi("status")}>{tUi("status")}</span></span>
                  </div>
                  <div>
                    {autoReply.escalated ? (
                      <Badge className="text-xs font-medium bg-red-100 text-red-800 border-red-200">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        <span><span key={tUi("escalated")}>{tUi("escalated")}</span></span>
                      </Badge>
                    ) : autoReply.forwarded ? (
                      <Badge className="text-xs font-medium bg-blue-100 text-blue-800 border-blue-200">
                        <Forward className="w-3 h-3 mr-1" />
                        <span><span key={tUi("forwarded")}>{tUi("forwarded")}</span></span>
                      </Badge>
                    ) : autoReply.skip_reason === "human_replied" ? (
                      <Badge className="text-xs font-medium bg-teal-100 text-teal-800 border-teal-200">
                        <UserCheck className="w-3 h-3 mr-1" />
                        <span><span key={tUi("humanReplied")}>{tUi("humanReplied")}</span></span>
                      </Badge>
                    ) : autoReply.skipped ? (
                      <Badge className="text-xs font-medium bg-gray-100 text-gray-800 border-gray-200">
                        <SkipForward className="w-3 h-3 mr-1" />
                        <span><span key={tUi("skipped")}>{tUi("skipped")}</span></span>
                      </Badge>
                    ) : autoReply.sent_at ? (
                      <Badge className="text-xs font-medium bg-green-100 text-green-800 border-green-200">
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        <span><span key={tUi("sent")}>{tUi("sent")}</span></span>
                      </Badge>
                    ) : autoReply.draft_created ? (
                      <Badge className="text-xs font-medium bg-purple-100 text-purple-800 border-purple-200">
                        <FileEdit className="w-3 h-3 mr-1" />
                        <span><span key={tUi("draft")}>{tUi("draft")}</span></span>
                      </Badge>
                    ) : autoReply.dry_run ? (
                      <Badge className="text-xs font-medium bg-yellow-100 text-yellow-800 border-yellow-200">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        <span><span key={tUi("dryRun")}>{tUi("dryRun")}</span></span>
                      </Badge>
                    ) : autoReply.error_message ? (
                      <Badge variant="destructive" className="text-xs">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        <span><span key={tUi("failed")}>{tUi("failed")}</span></span>
                      </Badge>
                    ) : (
                      <Badge className="text-xs font-medium bg-blue-100 text-blue-800 border-blue-200">
                        <span key={tUi("pending")}>{tUi("pending")}</span></Badge>
                    )}
                  </div>
                </div>
                {autoReply.sent_at && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        <span key={tUi("sentAt")}>{tUi("sentAt")}</span></span>
                    </div>
                    <div className="text-sm text-gray-900">
                      {formatDateTz(autoReply.sent_at, userTimezone, tUi, uiDateLocale)}
                    </div>
                  </div>
                )}
                {autoReply.classification_label && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        <span key={tUi("classification")}>{tUi("classification")}</span></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        className={cn(
                          "text-xs font-medium",
                          getQualificationColor(autoReply.classification_label)
                        )}
                      >
                        {autoReply.classification_label}
                      </Badge>
                      {autoReply.classification_confidence && (
                        <span className="text-xs text-gray-500"><TranslationText>{tUi.rich("value0Confidence", { value0: String((autoReply.classification_confidence * 100).toFixed(
                            0
                          )) })}</TranslationText></span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Error Message */}
              {autoReply.error_message && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong><span key={tUi("error")}>{tUi("error")}</span></strong> {autoReply.error_message}
                  </AlertDescription>
                </Alert>
              )}

              {/* Escalated Notice */}
              {autoReply.escalated && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    <div className="space-y-2">
                      <p>
                        <span><span key={tUi("thisCustomerWas")}>{tUi("thisCustomerWas")}</span></span> <strong><span key={tUi("escalated2")}>{tUi("escalated2")}</span></strong> <span><span key={tUi("toHumanSupportNoAutoReplyWasSent")}>{tUi("toHumanSupportNoAutoReplyWasSent")}</span></span>
                      </p>
                      {autoReply.escalation_reason && (
                        <p className="text-sm">
                          <strong><span key={tUi("reason")}>{tUi("reason")}</span></strong> {autoReply.escalation_reason}
                        </p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Forwarded Notice */}
              {autoReply.forwarded && (
                <Alert className="border-blue-200 bg-blue-50">
                  <Forward className="h-4 w-4 text-blue-600" />
                  <AlertDescription className="text-blue-800">
                    <div className="space-y-1">
                      <p>
                        <span><span key={tUi("thisEmailWas")}>{tUi("thisEmailWas")}</span></span> <strong><span key={tUi("forwarded2")}>{tUi("forwarded2")}</span></strong> <span><span key={tUi("insteadOfAutoReplying")}>{tUi("insteadOfAutoReplying")}</span></span>
                      </p>
                      {autoReply.forwarded_to && (
                        <p className="text-sm">
                          <strong><span key={tUi("forwardedTo")}>{tUi("forwardedTo")}</span></strong> {autoReply.forwarded_to}
                        </p>
                      )}
                      {autoReply.forwarded_reason && (
                        <p className="text-sm">
                          <strong><span key={tUi("reason")}>{tUi("reason")}</span></strong> {autoReply.forwarded_reason}
                        </p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Sender Blacklisted Notice */}
              {autoReply.skip_reason === "sender_blacklisted" && (
                <Alert className="border-red-200 bg-red-50">
                  <UserX className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    <div className="space-y-1">
                      <p><span><span key={tUi("thisSenderIs")}>{tUi("thisSenderIs")}</span></span> <strong><span key={tUi("blacklisted")}>{tUi("blacklisted")}</span></strong><span><span key={tUi("autoReplyWasSkipped")}>{tUi("autoReplyWasSkipped")}</span></span></p>
                      {autoReply.blacklist_reason && (
                        <p className="text-sm"><strong><span key={tUi("reason")}>{tUi("reason")}</span></strong> {autoReply.blacklist_reason}</p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Skipped Notice */}
              {autoReply.skipped && autoReply.skip_reason !== "sender_blacklisted" && (
                <Alert className="border-gray-200 bg-gray-50">
                  <SkipForward className="h-4 w-4 text-gray-600" />
                  <AlertDescription className="text-gray-800">
                    <div className="space-y-1">
                      <p>
                        <span><span key={tUi("aIDecided")}>{tUi("aIDecided")}</span></span> <strong><span key={tUi("notToReply")}>{tUi("notToReply")}</span></strong> <span><span key={tUi("toThisEmail")}>{tUi("toThisEmail")}</span></span>
                      </p>
                      {autoReply.skip_reason && (
                        <p className="text-sm">
                          <strong><span key={tUi("reason")}>{tUi("reason")}</span></strong> {autoReply.skip_reason}
                        </p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Human Replied Notice */}
              {autoReply.skip_reason === "human_replied" && (
                <Alert className="border-teal-200 bg-teal-50">
                  <UserCheck className="h-4 w-4 text-teal-600" />
                  <AlertDescription className="text-teal-800">
                    <span key={tUi("aTeamMemberHasAlreadyRepliedInThis")}>{tUi("aTeamMemberHasAlreadyRepliedInThis")}</span></AlertDescription>
                </Alert>
              )}

              {/* Draft Notice */}
              {autoReply.draft_created &&
                !autoReply.escalated &&
                !autoReply.forwarded &&
                !autoReply.skipped && (
                  <Alert className="border-purple-200 bg-purple-50">
                    <FileEdit className="h-4 w-4 text-purple-600" />
                    <AlertDescription className="text-purple-800">
                      <span><span key={tUi("thisReplyWasSavedAsA")}>{tUi("thisReplyWasSavedAsA")}</span></span> <strong><span key={tUi("draft2")}>{tUi("draft2")}</span></strong> <span><span key={tUi("inTheSenderSInboxYouCanReview")}>{tUi("inTheSenderSInboxYouCanReview")}</span></span>
                    </AlertDescription>
                  </Alert>
                )}

              {/* Dry Run Notice */}
              {autoReply.dry_run &&
                !autoReply.sent_at &&
                !autoReply.draft_created &&
                !autoReply.escalated &&
                !autoReply.forwarded &&
                !autoReply.skipped && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>
                      <span><span key={tUi("thisReplyWasGeneratedIn")}>{tUi("thisReplyWasGeneratedIn")}</span></span> <strong><span key={tUi("dryRunMode")}>{tUi("dryRunMode")}</span></strong>{" "}
                      <span><span key={tUi("andWasNotActuallySentToTheRecipient")}>{tUi("andWasNotActuallySentToTheRecipient")}</span></span>
                    </AlertDescription>
                  </Alert>
                )}

              {/* Thread History (Collapsible) */}
              {autoReply.thread_history && autoReply.thread_history.length > 0 && (
                <Collapsible
                  open={threadHistoryExpanded}
                  onOpenChange={setThreadHistoryExpanded}
                >
                  <div className="border rounded-lg overflow-hidden">
                    <CollapsibleTrigger className="w-full p-3 flex items-center justify-between bg-green-50 hover:bg-green-100 transition-colors">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          <span key={tUi("threadHistory")}>{tUi("threadHistory")}</span></span>
                        <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                          {autoReply.thread_history.length}
                        </Badge>
                      </div>
                      {threadHistoryExpanded ? (
                        <ChevronUp className="w-4 h-4 text-green-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-green-600" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 space-y-3 bg-white">
                        {autoReply.thread_history.map((msg) => (
                          <div
                            key={msg.id}
                            className={cn(
                              "flex",
                              msg.is_inbound ? "justify-start" : "justify-end"
                            )}
                          >
                            <div
                              className={cn(
                                "max-w-[80%] rounded-lg p-3 space-y-1",
                                msg.is_inbound
                                  ? "bg-gray-100 text-gray-900"
                                  : "bg-blue-100 text-blue-900"
                              )}
                            >
                              <div className="flex items-center gap-2 text-xs opacity-70">
                                <span className="font-medium flex items-center gap-1">
                                  {msg.is_inbound ? msg.from_email : (
                                    msg.is_agent_reply ? (
                                      <><TranslationText>{tUi.rich("tag0Tag0AIAgent", { tag0: () => (<Bot className="w-3 h-3" />) })}</TranslationText></>
                                    ) : (
                                      <><TranslationText>{tUi.rich("tag0Tag0Human", { tag0: () => (<User className="w-3 h-3" />) })}</TranslationText></>
                                    )
                                  )}
                                </span>
                                <span>{formatDateTz(msg.received_at, userTimezone, tUi, uiDateLocale)}</span>
                              </div>
                              {msg.body_html ? (
                                <div
                                  className="prose prose-sm max-w-none text-sm"
                                  dangerouslySetInnerHTML={{
                                    __html: msg.body_html,
                                  }}
                                />
                              ) : (
                                <pre className="whitespace-pre-wrap text-sm font-sans">
                                  {msg.body_text}
                                </pre>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}

              {/* Original Email (Collapsible) */}
              {autoReply.original_email && (
                <Collapsible
                  open={originalEmailExpanded}
                  onOpenChange={setOriginalEmailExpanded}
                >
                  <div className="border rounded-lg overflow-hidden">
                    <CollapsibleTrigger className="w-full p-3 flex items-center justify-between bg-blue-50 hover:bg-blue-100 transition-colors">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                          <span key={tUi("originalEmail")}>{tUi("originalEmail")}</span></span>
                        {autoReply.original_email.subject && (
                          <span className="text-xs text-blue-600 truncate max-w-[300px]">
                            — {autoReply.original_email.subject}
                          </span>
                        )}
                      </div>
                      {originalEmailExpanded ? (
                        <ChevronUp className="w-4 h-4 text-blue-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-blue-600" />
                      )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="p-4 space-y-4 bg-white">
                        {/* Email metadata */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {autoReply.original_email.from && (
                            <div className="flex items-start gap-2">
                              <User className="w-4 h-4 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-gray-500"><span key={tUi("from")}>{tUi("from")}</span></span>
                                <span className="text-gray-900">
                                  {autoReply.original_email.from}
                                </span>
                              </div>
                            </div>
                          )}
                          {autoReply.original_email.date && (
                            <div className="flex items-start gap-2">
                              <Clock className="w-4 h-4 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-gray-500"><span key={tUi("date")}>{tUi("date")}</span></span>
                                <span className="text-gray-900">
                                  {formatDateTz(autoReply.original_email.date, userTimezone, tUi, uiDateLocale)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between">
                          {autoReply.original_email.subject && (
                            <div className="flex items-start gap-2 text-sm">
                              <FileText className="w-4 h-4 text-gray-400 mt-0.5" />
                              <div>
                                <span className="text-gray-500"><span key={tUi("subject")}>{tUi("subject")}</span></span>
                                <span className="text-gray-900 font-medium">
                                  {autoReply.original_email.subject}
                                </span>
                              </div>
                            </div>
                          )}
                          {showCrmLink && autoReply.original_email.crmExternalUrl && (
                            <a
                              href={autoReply.original_email.crmExternalUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline flex-shrink-0"
                            >
                              <span><span key={"CRM"}>{"CRM"}</span></span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        {/* Email content */}
                        <div className="border-t pt-3">
                          {autoReply.original_email.body_html ? (
                            <div
                              className="prose prose-sm max-w-none text-sm"
                              dangerouslySetInnerHTML={{
                                __html: autoReply.original_email.body_html,
                              }}
                            />
                          ) : (
                            <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">
                              {autoReply.original_email.body_text ||
                                autoReply.original_email.snippet ||
                                tUi("noContentAvailable")}
                            </pre>
                          )}
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              )}

              {/* Reply Text */}
              {!autoReply.skipped && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4 text-purple-500" />
                      <span className="text-sm font-medium text-gray-700">
                        <span key={tUi("generatedReply")}>{tUi("generatedReply")}</span></span>
                    </div>
                    {autoReply.ai_request_log_id && (
                      <a
                        href={`/?page=ai-logs&log_id=${autoReply.ai_request_log_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        <ExternalLink className="w-3 h-3" />
                        <span><span key={tUi("viewAILog")}>{tUi("viewAILog")}</span></span>
                      </a>
                    )}
                  </div>
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="whitespace-pre-wrap text-sm text-gray-900">
                      {autoReply.reply_text}
                    </div>
                  </div>
                </div>
              )}

              {/* QA Feedback Section */}
              <Collapsible
                open={feedbackExpanded || !!feedback}
                onOpenChange={setFeedbackExpanded}
              >
                <div className="border rounded-lg overflow-hidden">
                  <CollapsibleTrigger className="w-full p-3 flex items-center justify-between bg-orange-50 hover:bg-orange-100 transition-colors">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-orange-600" />
                      <span className="text-sm font-medium text-orange-800">
                        <span key={tUi("qAFeedback")}>{tUi("qAFeedback")}</span></span>
                      {feedback?.status &&
                        getFeedbackStatusBadge(feedback.status)}
                      {loadingFeedback && (
                        <Loader2 className="w-3 h-3 animate-spin text-orange-600" />
                      )}
                    </div>
                    {feedbackExpanded || feedback ? (
                      <ChevronUp className="w-4 h-4 text-orange-600" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-orange-600" />
                    )}
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="p-4 space-y-4 bg-white">
                      {/* Existing feedback */}
                      {feedback && !isEditingFeedback && (
                        <div className="space-y-2">
                          <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap flex-1">
                                {feedback.feedback_text}
                              </p>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 flex-shrink-0"
                                onClick={handleEditFeedback}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                            </div>
                            {feedback.created_at && (
                              <p className="text-xs text-gray-500 mt-2">
                                <span><span key={tUi("added")}>{tUi("added")}</span></span> {formatDateTz(feedback.created_at, userTimezone, tUi, uiDateLocale)}
                              </p>
                            )}
                          </div>

                          {/* Status actions */}
                          {feedback.status && feedback.status !== "resolved" && (
                            <div className="flex items-center gap-2">
                              {feedback.status === "pending" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() =>
                                    handleUpdateStatus("in_progress")
                                  }
                                  disabled={updatingStatus}
                                >
                                  {updatingStatus ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <PlayCircle className="w-3 h-3 mr-1" />
                                  )}
                                  <span><span key={tUi("takeInProgress")}>{tUi("takeInProgress")}</span></span>
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-700 border-green-300 hover:bg-green-50"
                                onClick={() => handleUpdateStatus("resolved")}
                                disabled={updatingStatus}
                              >
                                {updatingStatus ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <CheckCheck className="w-3 h-3 mr-1" />
                                )}
                                <span><span key={tUi("markResolved")}>{tUi("markResolved")}</span></span>
                              </Button>
                            </div>
                          )}

                          {feedback.status === "resolved" &&
                            feedback.resolved_at && (
                              <p className="text-xs text-green-600">
                                <span><span key={tUi("resolved")}>{tUi("resolved")}</span></span> {formatDateTz(feedback.resolved_at, userTimezone, tUi, uiDateLocale)}
                              </p>
                            )}
                        </div>
                      )}

                      {/* Edit feedback mode */}
                      {feedback && isEditingFeedback && (
                        <div className="space-y-2">
                          <Textarea
                            placeholder={tUi("editYourFeedback")}
                            value={editFeedbackText}
                            onChange={(e) => setEditFeedbackText(e.target.value)}
                            className="min-h-[80px] text-sm"
                          />
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={handleCancelEdit}
                              disabled={savingFeedback}
                            >
                              <X className="w-3 h-3 mr-1" />
                              <span><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></span>
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveEditedFeedback}
                              disabled={
                                !editFeedbackText.trim() || savingFeedback
                              }
                            >
                              {savingFeedback ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <CheckCheck className="w-3 h-3 mr-1" />
                              )}
                              <span><span key={tUi("save")}>{tUi("save")}</span></span>
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Add new feedback */}
                      {!feedback && !loadingFeedback && (
                        <div className="space-y-2">
                          <Textarea
                            placeholder={tUi("leaveFeedbackAboutThisAIResponseIssuesImprovements")}
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            className="min-h-[80px] text-sm"
                          />
                          <div className="flex justify-end">
                            <Button
                              size="sm"
                              onClick={handleSaveFeedback}
                              disabled={!feedbackText.trim() || savingFeedback}
                            >
                              {savingFeedback ? (
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              ) : (
                                <Send className="w-3 h-3 mr-1" />
                              )}
                              <span><span key={tUi("submitFeedback")}>{tUi("submitFeedback")}</span></span>
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
